import { useState } from 'react'
import { ApiError, post } from '../lib/api.ts'
import { Modal } from './Modal.tsx'
import { useToast } from './Toast.tsx'
import { Button, Field, Input } from './ui.tsx'

/**
 * Cancelling an issued bill.
 *
 * Deliberately not a delete. An issued bill is a GST invoice with a number
 * from an unbroken series; removing one would leave a hole an inspector
 * would ask about, and the database refuses it outright. Cancelling keeps
 * the document and records why, which is the thing that can actually be
 * explained later.
 *
 * `paid` only changes what the dialog says. The money is already recorded
 * and cancelling does not un-record it -- somebody still has to hand it
 * back, and nothing here will do that for them.
 */
export function CancelBillDialog({ billId, version, paid, onClose, onDone }: {
  billId: string
  version: number
  paid?: boolean
  onClose: () => void
  onDone: () => void
}) {
  const toast = useToast()
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <Modal
      title="Cancel this bill"
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Keep it</Button>
          <Button variant="danger" loading={busy} disabled={reason.trim().length < 3}
            onClick={async () => {
              setBusy(true)
              try {
                await post(`/api/bills/${billId}/cancel`, { reason: reason.trim(), version })
                onDone()
              } catch (e) { toast.fail('Could not cancel', (e as ApiError).message) }
              finally { setBusy(false) }
            }}>
            Cancel bill
          </Button>
        </>
      }
    >
      <p className="muted" style={{ lineHeight: 1.7, marginBottom: 'var(--s-4)' }}>
        An issued bill is never deleted — it is cancelled, and the cancellation is part
        of the record. Once cancelled, a fresh bill can be raised from the prescription.
      </p>
      {paid ? (
        <p style={{
          lineHeight: 1.7, marginBottom: 'var(--s-4)', padding: 'var(--s-3) var(--s-4)',
          background: 'var(--warn-soft)', border: '1px solid var(--rule)',
          borderRadius: 'var(--radius)', fontSize: 'var(--text-sm)',
        }}>
          <b>This bill has been paid.</b> Cancelling it does not return the money —
          the payment stays on the record, and the refund is something somebody has
          to hand over. Say so in the reason.
        </p>
      ) : null}
      <Field label="Reason" htmlFor="cancel-reason" hint="Recorded against the bill and in the audit log">
        <Input id="cancel-reason" value={reason} autoFocus maxLength={500}
          onChange={e => setReason(e.target.value)} placeholder="Prescription revised" />
      </Field>
    </Modal>
  )
}
